import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, switchMap, map, of } from 'rxjs';
import { FilterApiService } from 'src/app/core/services/filter-api/filter-api.service';
import { ConfigurationApiActions } from '../../actions/configuration-api.actions';
import { HttpEventType } from '@angular/common/http';
import { DeviceStatisticsApiService } from 'src/app/core/services/device-statistics-api/device-statistics-api.service';

@Injectable()
export class ConfigurationApiEffects {
  loadSpaces$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConfigurationApiActions.loadSpaces),
      switchMap(({ tenantId, dataSetId }) =>
        this.configurationApi.getSpaces(tenantId, dataSetId).pipe(
          map((spaces) =>
            ConfigurationApiActions.loadSpacesSuccess({ spaces })
          ),
          catchError((error) =>
            of(ConfigurationApiActions.loadSpacesFailure({ error }))
          )
        )
      )
    )
  );

  loadShifts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConfigurationApiActions.loadShifts),
      exhaustMap(({ tenantId }) =>
        this.configurationApi.getShifts(tenantId).pipe(
          map((shifts) =>
            ConfigurationApiActions.loadShiftsSuccess({ shifts })
          ),
          catchError((error) =>
            of(ConfigurationApiActions.loadShiftsFailure({ error }))
          )
        )
      )
    )
  );

  updateDeviceConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConfigurationApiActions.updateDeviceConfiguration),
      exhaustMap(({ payload }) => {
        const formData = new FormData();
        formData.append(
          'file',
          payload.file,
          `${payload.siteName}-${payload.siteId}.csv`
        );
        formData.append('tenantId', payload.tenantId);
        formData.append('orgName', payload.orgName);
        formData.append('siteName', payload.siteName);
        formData.append('siteId', payload.siteId);
        const existingAssets =
          typeof payload.existingAssets === 'object'
            ? JSON.stringify(payload.existingAssets)
            : payload.existingAssets;
        formData.append('existingAssets', existingAssets);
        formData.append('source', 'FTAE');

        return this.deviceStatisticsApiService
          .updateDeviceConfiguration(formData, {
            reportProgress: true,
            observe: 'events',
          })
          .pipe(
            map((event) => {
              if (event.type === HttpEventType.UploadProgress) {
                const progress = Math.round(
                  100 * (event.loaded / (event.total || 1))
                );
                return ConfigurationApiActions.updateDeviceConfigurationProgress(
                  { progress }
                );
              } else if (event.type === HttpEventType.Response) {
                return ConfigurationApiActions.updateDeviceConfigurationSuccess(
                  { devicesInformation: event.body.devicesInformation }
                );
              } else {
                return { type: 'noop' };
              }
            }),
            catchError((error) =>
              of(
                ConfigurationApiActions.updateDeviceConfigurationFailure({
                  error,
                })
              )
            )
          );
      })
    )
  );

  loadDeviceConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConfigurationApiActions.loadDeviceConfiguration),
      exhaustMap(({ payload }) =>
        this.deviceStatisticsApiService.getDeviceConfiguration(payload).pipe(
          map((data) =>
            ConfigurationApiActions.loadDeviceConfigurationSuccess(data)
          ),
          catchError((error) =>
            of(
              ConfigurationApiActions.loadDeviceConfigurationFailure({ error })
            )
          )
        )
      )
    )
  );

  updateCSV$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConfigurationApiActions.updateCSV),
      exhaustMap(({ payload }) =>
        this.deviceStatisticsApiService.updateCsvAndDbFlag(payload).pipe(
          map((data: boolean) =>
            ConfigurationApiActions.updateCSVSuccess({ success: data })
          ),
          catchError((error) =>
            of(ConfigurationApiActions.updateCSVFailure({ error }))
          )
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private configurationApi: FilterApiService,
    private deviceStatisticsApiService: DeviceStatisticsApiService
  ) {}
}
